import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, PlugZap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, get, errMsg } from "@/lib/api";
import { StatusChip } from "@/components/app/StatusChip";

const CHIP = { connected: ["Completed", "Connected"], not_set_up: ["Skipped", "Not set up"], error: ["Failed", "Error"] };
export const ConnStatus = ({ status, testId }) => <StatusChip status={CHIP[status][0]} label={CHIP[status][1]} testId={testId} />;

function SecretField({ id, label, field, value, onChange }) {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="text-xs font-medium text-ink-secondary">{label}</label>
      <Input id={id} type="password" autoComplete="new-password" value={value} onChange={(e) => onChange(e.target.value)} data-testid={id}
        placeholder={field?.masked ? `${field.masked} (${field.source === "env" ? "from server environment" : "saved"}) · type to replace` : "Not set"} />
    </div>
  );
}

function TextField({ id, label, field, value, onChange }) {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="text-xs font-medium text-ink-secondary">{label}{field?.source === "env" && <span className="ml-1 text-ink-muted">(from server environment)</span>}</label>
      <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} data-testid={id} />
    </div>
  );
}

export function KeysCard({ title, purpose, status, testId, fields, saveUrl, onTest, testLabel = "Test", children }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(() => Object.fromEntries(fields.map((f) => [f.key, f.secret ? "" : f.field?.value || ""])));
  const [busy, setBusy] = useState(false);
  const [test, setTest] = useState(null);
  const save = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(fields.map((f) => [f.key, f.secret ? form[f.key] || null : form[f.key]]));
      const r = (await api.put(saveUrl, body)).data;
      toast.success(r.changed.length ? `Saved: ${r.changed.length} field(s) updated` : "Nothing changed");
      setForm((cur) => Object.fromEntries(Object.entries(cur).map(([k, v]) => [k, fields.find((f) => f.key === k)?.secret ? "" : v])));
      qc.invalidateQueries({ queryKey: ["connections"] });
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const runTest = async () => { setTest({ busy: true }); try { setTest(await onTest()); } catch (e) { setTest({ ok: false, detail: errMsg(e) }); } qc.invalidateQueries({ queryKey: ["connections"] }); };
  return (
    <section className="panel space-y-4 p-6" data-testid={testId}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><h2 className="text-base font-semibold">{title}</h2><p className="text-sm text-ink-secondary">{purpose}</p></div>
        <ConnStatus status={status} testId={`${testId}-status`} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {fields.map((f) => (f.secret ? SecretField : TextField)({ id: `${testId}-${f.key}`, label: f.label, field: f.field, value: form[f.key], onChange: (v) => setForm({ ...form, [f.key]: v }), key: f.key }))}
      </div>
      {children}
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={save} disabled={busy} data-testid={`${testId}-save`}>{busy && <Loader2 size={14} className="animate-spin" />} Save</Button>
        {onTest && <Button variant="outline" onClick={runTest} disabled={test?.busy} data-testid={`${testId}-test`}><PlugZap size={14} aria-hidden="true" /> {testLabel}</Button>}
        {test && !test.busy && <span role="status" className={test.ok ? "text-sm text-success" : "text-sm text-risk"} data-testid={`${testId}-test-result`}>{test.ok ? `Connected · ${test.latency_ms} ms${test.model ? ` · ${test.model}` : ""}` : test.detail}</span>}
      </div>
    </section>
  );
}

export const useConnections = () => useQuery({ queryKey: ["connections"], queryFn: () => get("/settings/connections") });
